import React, { Component } from "react";
import { connect } from "react-redux";
import { Wrapper } from "../../../atoms/Layout";
import Button from "../../../atoms/Button";
import Tooltip from "../../../atoms/Tooltip";
import { createAlert } from "../../../../actions/utils";
import { doCreateSendGridDomain, doGetSendGridDomain, doValidateSendGridDomain } from "../../../../actions/index";

interface DomainManagementProps {
    user: Mongo.clientAdmin;
}

interface DomainManagementState {
    sendGridDomain: string;
    sendGridDomainId: string | null;
    sendGridLoading: boolean;
    sendGridResult: any;
    sendGridStep: string | null;
    sendGridMessage: string | null;
    sendGridMessageType: 'success' | 'error' | 'info' | null;
    validationNeeded: boolean;
}

class DomainManagement extends Component<DomainManagementProps, DomainManagementState> {
    constructor(props: DomainManagementProps) {
        super(props);
        this.state = {
            sendGridDomain: "",
            sendGridDomainId: null,
            sendGridLoading: false,
            sendGridResult: null,
            sendGridStep: null,
            sendGridMessage: null,
            sendGridMessageType: null,
            validationNeeded: false
        };
        this._handleCreateDomain = this._handleCreateDomain.bind(this);
        this._handleValidateDomain = this._handleValidateDomain.bind(this);
    }

    async _handleCreateDomain() {
        let domain = this.state.sendGridDomain ? this.state.sendGridDomain.trim() : "";
        // Basic domain validation: must be non-empty, contain at least one dot, and not start/end with dot or whitespace
        const domainRegex = /^(?!\.|\s)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?<!\.|\s)$/;
        if (!domain || !domainRegex.test(domain)) {
            createAlert("Please enter a valid domain (e.g., example.com)", "error");
            return;
        }
        this.setState({ sendGridLoading: true, sendGridResult: null, sendGridStep: 'creating', sendGridDomainId: null, sendGridMessage: null });
        try {
            const result = await doCreateSendGridDomain(domain.trim());
            // Automatically fetch domain info in the background
            const domainInfo = await doGetSendGridDomain(domain.trim());
            const domainData = Array.isArray(domainInfo) && domainInfo.length > 0 ? domainInfo[0] : domainInfo;

            this.setState({
                sendGridResult: domainData,
                sendGridLoading: false,
                sendGridStep: 'created',
                sendGridDomainId: domainData.id || result.id,
                sendGridMessage: `Domain created successfully! Click "Validate Domain" to complete the setup.`,
                sendGridMessageType: 'success',
                validationNeeded: !domainData.valid
            });
            createAlert("Domain created successfully! Click Validate Domain to complete setup.", "success");
        } catch (err) {
            let errorMsg = typeof err === 'string' ? err : (err instanceof Error ? err.message : "Failed to create domain");
            errorMsg = errorMsg.replace(/\s*\(Status:\s*\d+\)\s*$/, '');
            this.setState({
                sendGridLoading: false,
                sendGridStep: null,
                sendGridResult: { error: errorMsg },
                sendGridDomainId: null,
                sendGridMessage: `Failed to create domain: ${errorMsg}`,
                sendGridMessageType: 'error'
            });
            createAlert(errorMsg, "error");
        }
    }

    async _handleValidateDomain() {
        let domain = this.state.sendGridDomain ? this.state.sendGridDomain.trim() : "";
        const domainRegex = /^(?!\.|\s)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?<!\.|\s)$/;
        if (!domain || !domainRegex.test(domain)) {
            createAlert("Please enter a valid domain (e.g., example.com)", "error");
            return;
        }

        this.setState({ sendGridLoading: true, sendGridStep: 'fetching', sendGridMessage: null });
        try {
            // Always fetch domain info first
            const result = await doGetSendGridDomain(domain.trim());
            const domainData = Array.isArray(result) && result.length > 0 ? result[0] : result;

            if (!domainData || !domainData.id) {
                this.setState({
                    sendGridLoading: false,
                    sendGridStep: null,
                    sendGridResult: null,
                    sendGridDomainId: null,
                    sendGridMessage: `Domain not found. Please create it first.`,
                    sendGridMessageType: 'info',
                    validationNeeded: false
                });
                createAlert("Domain not found. Please create it first.", "warning");
                return;
            }

            if (domainData.valid === true) {
                this.setState({
                    sendGridResult: domainData,
                    sendGridLoading: false,
                    sendGridStep: 'fetched',
                    sendGridDomainId: domainData.id,
                    sendGridMessage: `Domain is already validated!`,
                    sendGridMessageType: 'success',
                    validationNeeded: false
                });
                createAlert("Domain is already validated!", "success");
                return;
            }

            // If not validated, proceed to validate
            this.setState({ sendGridStep: 'validating', sendGridLoading: true, sendGridDomainId: domainData.id });
            const validationResult = await doValidateSendGridDomain(domain.trim(), parseInt(domainData.id, 10));
            this.setState({
                sendGridResult: validationResult,
                sendGridLoading: false,
                sendGridStep: 'validated',
                sendGridMessage: validationResult.valid
                    ? `Domain validation successful!`
                    : `Domain validation failed.`,
                sendGridMessageType: validationResult.valid ? 'success' : 'error',
                validationNeeded: !validationResult.valid
            });
            if (validationResult.valid) {
                createAlert("Domain validated successfully!", "success");
            } else {
                createAlert("Domain validation failed", "error");
            }
        } catch (err) {
            let errorMsg = typeof err === 'string' ? err : (err instanceof Error ? err.message : "Failed to fetch/validate domain");
            errorMsg = errorMsg.replace(/\s*\(Status:\s*\d+\)\s*$/, '');
            this.setState({
                sendGridLoading: false,
                sendGridStep: null,
                sendGridResult: { error: errorMsg },
                sendGridDomainId: null,
                sendGridMessage: `Failed to fetch/validate domain: ${errorMsg}`,
                sendGridMessageType: 'error',
                validationNeeded: false
            });
            createAlert(errorMsg, "error");
        }
    }

    render() {
        return (
            <Wrapper>
                <div className="wrapper wrapper-content">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="ibox float-e-margins">
                                <div className="ibox-title">
                                    <h5>Domain Management</h5>
                                </div>
                                <div className="ibox-content">
                                    <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#f0f8ff", border: "1px solid #b0d4f1", borderRadius: "4px", fontSize: "13px" }}>
                                        <strong>Setup Steps:</strong>
                                        <ol style={{ margin: "5px 0 0 0", paddingLeft: "20px" }}>
                                            <li>Create Domain - Creates and configures the domain in SendGrid automatically</li>
                                            <li>Get Domain - Retrieves existing domain info and checks validation status</li>
                                            <li>Validate Domain - Verifies the domain is ready to use</li>
                                        </ol>
                                        {this.state.sendGridDomainId && (
                                            <div style={{ marginTop: "8px", color: "#0066cc", fontWeight: 500 }}>
                                                <strong>Domain ID:</strong> {this.state.sendGridDomainId}
                                            </div>
                                        )}
                                    </div>
                                    <div className="form__row">
                                        <div className="form__group" style={{ width: "100%", marginBottom: "20px" }}>
                                            <label htmlFor="sendGridDomain" className="form__label">
                                                Domain:&nbsp;
                                                <Tooltip>
                                                    Enter the domain to manage (e.g., example.com)
                                                </Tooltip>
                                            </label>
                                            <input
                                                id="sendGridDomain"
                                                type="text"
                                                className="form__value"
                                                placeholder="e.g., example.com"
                                                value={this.state.sendGridDomain}
                                                onChange={event =>
                                                    this.setState({
                                                        sendGridDomain: event.target.value,
                                                        sendGridResult: null,
                                                        sendGridStep: null
                                                    })
                                                }
                                                disabled={this.state.sendGridLoading}
                                            />
                                        </div>
                                    </div>
                                    <div className="form__row" style={{ gap: "10px", display: "flex", flexWrap: "wrap", marginBottom: "20px" }}>
                                        <Button
                                            onClick={this._handleCreateDomain}
                                            disabled={this.state.sendGridLoading || !this.state.sendGridDomain.trim()}
                                        >
                                            {this.state.sendGridLoading && this.state.sendGridStep === 'creating' ? "Creating..." : "Create Domain"}
                                        </Button>
                                        <Button
                                            onClick={this._handleValidateDomain}
                                            disabled={this.state.sendGridLoading || !this.state.sendGridDomain.trim()}
                                        >
                                            {this.state.sendGridLoading && (this.state.sendGridStep === 'fetching' || this.state.sendGridStep === 'validating') ? (this.state.sendGridStep === 'fetching' ? "Fetching..." : "Validating...") : "Validate Domain"}
                                        </Button>
                                    </div>
                                    {this.state.sendGridResult && this.state.sendGridMessage && (
                                        <div style={{
                                            marginTop: "15px",
                                            border: `1px solid ${this.state.sendGridMessageType === 'success' ? '#28a745' : this.state.sendGridMessageType === 'error' ? '#dc3545' : '#0066cc'}`,
                                            borderRadius: "4px",
                                            padding: "12px 15px",
                                            backgroundColor: this.state.sendGridMessageType === 'success' ? '#d4edda' : this.state.sendGridMessageType === 'error' ? '#f8d7da' : '#d1ecf1',
                                            fontSize: "14px",
                                            color: "#333",
                                            lineHeight: "1.5"
                                        }}>
                                            <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                                                {this.state.sendGridMessageType === 'success' ? '✓ Success' : this.state.sendGridMessageType === 'error' ? 'Error' : 'Info'}
                                            </div>
                                            <div>{this.state.sendGridMessage}</div>
                                            {/* DNS Records Section */}
                                            {this.state.sendGridResult.dns && (
                                                <div style={{ marginTop: '18px' }}>
                                                    <strong>DNS Records to Add:</strong>
                                                    <ul style={{ marginTop: 8 }}>
                                                        {Object.entries(this.state.sendGridResult.dns).map(([recordType, records]: [string, any]) => (
                                                            <li key={recordType} style={{ marginBottom: 8 }}>
                                                                <span style={{ fontWeight: 500 }}>{recordType}:</span>
                                                                <ul style={{ marginTop: 4 }}>
                                                                    {Array.isArray(records)
                                                                        ? records.map((rec, idx) => (
                                                                            <li key={idx} style={{ fontFamily: 'monospace', fontSize: 13 }}>
                                                                                {typeof rec === 'object' ? JSON.stringify(rec) : rec}
                                                                            </li>
                                                                        ))
                                                                        : <li style={{ fontFamily: 'monospace', fontSize: 13 }}>{typeof records === 'object' ? JSON.stringify(records) : records}</li>
                                                                    }
                                                                </ul>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {/* DNS Validation Results Section */}
                                            {this.state.sendGridResult.validation_results && (
                                                <div style={{ marginTop: '18px' }}>
                                                    <strong>DNS Validation Results:</strong>
                                                    <ul style={{ marginTop: 8 }}>
                                                        {Object.entries(this.state.sendGridResult.validation_results).map(([record, result]: [string, any]) => (
                                                            <li key={record} style={{ marginBottom: 8 }}>
                                                                <span style={{ fontWeight: 500 }}>{record}:</span>
                                                                <span style={{ marginLeft: 8, color: result.valid ? '#28a745' : '#dc3545', fontWeight: 500 }}>
                                                                    {result.valid ? 'Valid' : 'Invalid'}
                                                                </span>
                                                                {result.reason && (
                                                                    <span style={{ marginLeft: 12, color: '#555' }}>
                                                                        Reason: {result.reason}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Wrapper>
        );
    }
}

const mapStateToProps = state => ({
    user: state.session.admin
});

export default connect(mapStateToProps)(DomainManagement);
